import { useMemo, useState } from "react";
import { Info, Maximize2, Minimize2 } from "lucide-react";

import { ControlPanel, SECTION_STACK_CLASS } from "@pervasivemind/dashboard-render";
import { useT } from "@/i18n";
import { cn } from "@/lib/utils";
import {
  DashboardSections,
  PreviewFilterBar,
} from "@/pages/builder/preview/PreviewModal";
import type { UseBuilderDraft } from "@/hooks/useBuilderDraft";
import { withGroupFilters } from "@/lib/report-group-preview";

interface RealDashboardCanvasProps {
  draft: UseBuilderDraft;
  /** Iter 22 — when true, the parent has hidden the inspector pane.
   *  We swap the maximize affordance for a minimize one. */
  expanded?: boolean;
  /** Callback to flip the expanded state. When undefined the toggle
   *  button doesn't render — preserves the canvas usable for callers
   *  that don't want the toggle. */
  onToggleExpanded?: () => void;
}

/**
 * Iter 21 — the Dashboard tab's canvas, replacing the legacy
 * ``DiagramView`` placeholder blocks.
 *
 * Reuses ``DashboardSections`` + ``PreviewFilterBar`` from the
 * ``PreviewModal`` (same render pipeline = same fidelity as the
 * full-screen preview). The differences vs PreviewModal:
 *
 *   1. Embedded in the Dashboard tab's left pane (not a modal).
 *   2. Adds ``onSelectItem`` / ``onSelectSection`` callbacks so
 *      clicking a rendered item opens its read-only inspector in
 *      the right pane.
 *   3. Selection ring highlights the currently-selected item.
 *
 * Iter 22 additions:
 *   * ``expanded`` / ``onToggleExpanded`` toggle hides the inspector
 *     pane so the canvas takes 100% width. Useful for eyeballing
 *     chart proportions at full fidelity.
 *   * Filter-bar wrapper is rendered only when the report has
 *     global filters — eliminates the empty white strip between the
 *     read-only banner and the first KPI row on reports like
 *     ``dashboard_explorer`` whose filters live in sections.
 *
 * Structural edits are NOT possible from this canvas — the
 * "Read-only" banner makes that explicit. To add/remove/reorder
 * items the admin uses Claude with the report-builder skill.
 */
export function RealDashboardCanvas({
  draft,
  expanded = false,
  onToggleExpanded,
}: RealDashboardCanvasProps): JSX.Element {
  const { t } = useT();
  const [activeFilters, setActiveFilters] = useState<Record<string, string>>({});

  // A report-group member previews with its GROUP's filters (render only).
  const previewDashboard = useMemo(
    () => withGroupFilters(draft.dashboard, draft.draft),
    [draft.dashboard, draft.draft],
  );
  const groupError = draft.draft?.group?.error ?? null;
  const globalFilters = previewDashboard.global_filters ?? [];
  const hasGlobalFilters = globalFilters.length > 0;

  return (
    <div className="flex h-full flex-col bg-neutral-50">
      {/* Was a full-width AMBER banner, on every visit, not
          dismissible. Amber means "something is wrong"; this is a
          permanent property of the surface — the canvas renders, it
          does not edit structure. A standing warning for a normal
          state is how people learn to stop reading amber, so it is now
          a quiet neutral strip with an info glyph. Same words, same
          place, no false alarm. */}
      <div className="flex items-start gap-2 border-b border-neutral-200 bg-neutral-50 px-4 py-2">
        <Info
          size={14}
          aria-hidden="true"
          className="mt-0.5 shrink-0 text-neutral-500"
        />
        <p className="flex-1 text-xs text-neutral-600">
          <span className="font-semibold text-neutral-700">
            {t("canvas.readOnly.label")}
          </span>{" "}
          {t("canvas.readOnly.hint")}
        </p>
        {onToggleExpanded && (
          <button
            type="button"
            onClick={onToggleExpanded}
            className={cn(
              "shrink-0 grid h-6 w-6 place-items-center rounded",
              "text-neutral-500 hover:bg-neutral-200 hover:text-neutral-800",
              "transition-colors",
            )}
            title={
              expanded ? t("canvas.collapse") : t("canvas.expand")
            }
            aria-label={
              expanded ? t("canvas.collapse") : t("canvas.expand")
            }
          >
            {expanded ? (
              <Minimize2 className="h-3.5 w-3.5" />
            ) : (
              <Maximize2 className="h-3.5 w-3.5" />
            )}
          </button>
        )}
      </div>

      {groupError && (
        <div
          role="alert"
          className="border-b border-amber-300 bg-amber-50 px-4 py-2 text-xs font-medium text-amber-900"
        >
          {t("reportGroup.memberError", { error: groupError })}
        </div>
      )}

      {hasGlobalFilters && (
        // The same light ``ControlPanel`` the PreviewModal and the
        // portal page put around the filters (``PreviewFilterBar``
        // renders ``surface="light"``). It used to sit on the old dark
        // header gradient, so the canvas showed a panel the portal no
        // longer has.
        <div className="border-b border-neutral-200 px-4 py-3">
          <ControlPanel>
            <PreviewFilterBar
              draft={draft}
              dashboard={previewDashboard}
              filters={globalFilters}
              active={activeFilters}
              onChange={setActiveFilters}
            />
          </ControlPanel>
        </div>
      )}

      <div
        className="flex-1 overflow-y-auto p-4"
        onClick={() => {
          // Clicking the canvas background (not a section/item)
          // clears the selection so the inspector returns to
          // ``DashboardInspector``. Clicks on rendered items
          // stop propagation in the wrapSelectable helper.
          draft.clearSelection();
        }}
      >
        {/* Shared section-stack gap (PR-3 §1) — keeps the canvas, the
            PreviewModal and the portal page pixel-identical. */}
        <div className={SECTION_STACK_CLASS}>
          <DashboardSections
            sections={draft.dashboard.sections}
            report={draft.report}
            dashboard={previewDashboard}
            activeFilters={activeFilters}
            projectSlug={draft.draft?.project_slug}
            onSelectItem={(sectionId, itemId) =>
              draft.selectItem(sectionId, itemId)
            }
            onSelectSection={(sectionId) => draft.selectSection(sectionId)}
            selectedItemId={draft.selection.itemId}
          />
        </div>
      </div>
    </div>
  );
}
